import { NError, NSuccess } from '@src/configs/const.config'
import { checkAuth } from '@libs/localStorage'
import { TResApi, TResApiErr } from '@src/configs/interface.config'
import { TDealerTier, TTierRankingPolicy, TUpdateDealerTier, TUpdateTierRankingPolicy } from '@src/modules'
import { notification } from 'antd'
import { useMutation, useQuery } from 'react-query'

import { getDealerTierLadder, getRankingPolicy, publishRankingPolicy, updateDealerTier, updateRankingPolicy } from '../apis/dealer-tier-admin'
import { queryClient } from '..'

export const DEALER_TIER_LADDER = 'DEALER_TIER_LADDER'
export const RANKING_POLICY = 'RANKING_POLICY'

export const useDealerTierLadder = () => {
  const token = checkAuth()
  return useQuery<TResApi<TDealerTier[]>>(
    [DEALER_TIER_LADDER],
    () => getDealerTierLadder(token),
    { enabled: !!token },
  )
}

export const useUpdateDealerTier = () => {
  const token = checkAuth()
  return useMutation(
    ({ id, data }: { id: number; data: TUpdateDealerTier }) =>
      updateDealerTier(id, data, token),
    {
      onSuccess: (res: TResApi<TDealerTier>) => {
        queryClient.invalidateQueries([DEALER_TIER_LADDER])
        notification.success({ message: NSuccess, description: res?.message })
      },
      onError: (error: TResApiErr) =>
        notification.error({ message: NError, description: error?.message }),
    },
  )
}

export const useRankingPolicy = (year: number) => {
  const token = checkAuth()
  return useQuery<TResApi<TTierRankingPolicy>>(
    [RANKING_POLICY, year],
    () => getRankingPolicy(year, token),
    { enabled: !!token && !!year },
  )
}

export const useUpdateRankingPolicy = (year: number) => {
  const token = checkAuth()
  return useMutation(
    (data: TUpdateTierRankingPolicy) => updateRankingPolicy(year, data, token),
    {
      onSuccess: (res: TResApi<TTierRankingPolicy>) => {
        // Seed the cache with the saved policy so the form does not flash the old values.
        queryClient.setQueryData([RANKING_POLICY, year], res)
        queryClient.invalidateQueries([RANKING_POLICY, year])
        notification.success({ message: NSuccess, description: `Đã lưu cấu hình xếp hạng năm ${year}` })
      },
      onError: (error: TResApiErr) =>
        notification.error({ message: NError, description: error?.message }),
    },
  )
}

export const usePublishRankingPolicy = (year: number) => {
  const token = checkAuth()
  return useMutation(
    () => publishRankingPolicy(year, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RANKING_POLICY, year])
        notification.success({ message: NSuccess, description: `Đã ban hành cấu hình xếp hạng năm ${year}` })
      },
      onError: (error: TResApiErr) => {
        queryClient.invalidateQueries([RANKING_POLICY, year])
        const rawMsg = error?.message
        const description = typeof rawMsg === 'string' && rawMsg.toLowerCase().includes('published')
          ? 'Chính sách đã được ban hành'
          : (rawMsg as string)
        notification.error({ message: NError, description })
      },
    },
  )
}
